import {
  Badge,
  Button,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type {
  PersonaBriefAiEditCell,
  PersonaBriefContentPatch,
  PersonaBriefDto,
  PersonaBriefHookPackage,
} from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import { HighlightText } from "@/features/opportunities/components/HighlightText";
import { formatPersonaBriefForCopy } from "@/features/persona-opportunities/lib/persona-brief-clipboard";
import {
  PERSONA_BRIEF_AGE_RANGE_OPTIONS,
  PERSONA_BRIEF_DURATION_OPTIONS,
  PERSONA_BRIEF_GENDER_OPTIONS,
  PERSONA_BRIEF_HOOK_PACKAGE_OPTIONS,
  translatePersonaBriefClosedListValue,
  withCurrentOption,
} from "@/features/persona-opportunities/lib/persona-brief-options";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

import { PersonaOpportunitySelectCheckbox } from "../../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import {
  PERSONA_SCRIPT_COPY_RESET_MS,
  PersonaScriptEditableCell,
} from "./PersonaScriptEditableCell.js";
import { PersonaScriptTitleEditor } from "./PersonaScriptTitleEditor.js";

const ICON_BUTTON_CLASS =
  "h-[34px] w-[34px] min-h-11 min-w-11 p-0 has-[>svg]:p-0 [&_svg]:size-3.5 [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px] [@media(hover:hover)_and_(pointer:fine)]:min-w-[34px]";
const FINE_POINTER_FOOTER_H =
  "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px]";
const AI_BUTTON_CLASS =
  "h-[34px] min-h-11 border border-[var(--ai-border)] bg-[var(--ai-fill-light)] text-[var(--ai-text)] hover:bg-[var(--ai-fill-medium)] [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px]";
const LOADING_BUTTON_CLASS =
  "ring-1 ring-[var(--ai)] disabled:opacity-80 [&_svg]:animate-spin";
const SEND_BUTTON_CLASS = `h-[34px] ${FINE_POINTER_FOOTER_H} gap-1.5 px-3.5 text-[13px] font-semibold`;
const MONO_LABEL =
  "shrink-0 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground";
const COL_GRID = "grid grid-cols-1 sm:grid-cols-[4.75rem_minmax(0,1fr)_minmax(0,1fr)]";
const BODY_TEXT = "text-secondary-subtle-foreground";
const CREATOR_COUNT_DISPLAY = 1;
const CHROME_SELECT_TRIGGER =
  "h-8 w-auto min-w-[8.5rem] px-2 text-[12.5px]";

export interface PersonaBriefCardProps {
  brief: PersonaBriefDto;
  canEdit?: boolean;
  isSelected?: boolean;
  onToggleSelected?: () => void;
  expanded?: boolean;
  onExpandedChange?: (open: boolean) => void;
  onPatch?: (patch: PersonaBriefContentPatch) => void | Promise<unknown>;
  onToggleFavorite?: () => void;
  onOpenHistory?: () => void;
  onAiEdit?: () => void;
  isAiEditing?: boolean;
  onApplyAi?: (cellKey: string, instruction: string) => void;
  aiEditKey?: string | null;
  aiBusyKey?: string | null;
  flashKey?: string | null;
  onStartAi?: (key: string) => void;
  onStopAi?: () => void;
  searchQuery?: string;
}

function briefReferenceHref(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

async function writeClipboard(text: string): Promise<boolean> {
  const copied = navigator.clipboard?.writeText(text);
  if (!copied) return false;
  try {
    await copied;
    return true;
  } catch {
    return false;
  }
}

function isBrandOverridden(brand: PersonaBriefDto["brand"]): boolean {
  return (
    brand.productLink != null ||
    brand.font != null ||
    brand.logo != null ||
    brand.disclaimer != null
  );
}

function parseHookPackage(value: string): PersonaBriefHookPackage | null {
  if (value === "1") return 1;
  if (value === "4") return 4;
  return null;
}

function PaperPlaneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3.5 shrink-0"
      data-icon="send"
    >
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

function FieldLabel({ children }: { children: string }) {
  return <span className={MONO_LABEL}>{children}</span>;
}

function hookRowClass(index: number): string {
  return index % 2 === 1 ? "bg-muted" : "bg-background";
}

function ClosedListSelect({
  ariaLabel,
  value,
  searchQuery,
  options,
  formatOption,
  readOnly,
  disabled,
  onValueChange,
}: {
  ariaLabel: string;
  value: string;
  searchQuery: string;
  options: readonly string[];
  formatOption: (value: string) => string;
  readOnly?: boolean;
  disabled?: boolean;
  onValueChange: (value: string) => void;
}) {
  const display = formatOption(value);

  if (readOnly) {
    return (
      <span className="text-[12.5px] text-foreground">
        <HighlightText text={display} query={searchQuery} />
      </span>
    );
  }

  const items = withCurrentOption(options, value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger aria-label={ariaLabel} className={CHROME_SELECT_TRIGGER}>
        <SelectValue>
          <HighlightText text={display} query={searchQuery} />
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {items.map((option) => (
          <SelectItem key={option} value={option}>
            {formatOption(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BrandFieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 px-1.5 py-1">
      <span className="w-[104px] shrink-0 text-[12.5px] text-muted-foreground">{label}</span>
      <span className={cn("min-w-0 flex-1 text-[12.5px] leading-[1.55]", BODY_TEXT)}>{value}</span>
    </div>
  );
}

function BrandOverrideInput({
  id,
  label,
  value,
  disabled,
  onCommit,
}: {
  id: string;
  label: string;
  value: string | null;
  disabled?: boolean;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  function commit() {
    const next = draft.trim() === "" ? null : draft.trim();
    const current = value == null || value.trim() === "" ? null : value.trim();
    if (next === current) return;
    onCommit(next);
  }

  return (
    <div className="flex items-start gap-3 px-1.5 py-1">
      <label
        htmlFor={id}
        className="w-[104px] shrink-0 pt-1.5 text-[12.5px] text-muted-foreground"
      >
        {label}
      </label>
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
        className="h-8 flex-1 text-[12.5px]"
      />
    </div>
  );
}

function CollapsibleReveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div
        className={cn(
          "min-h-0 overflow-hidden transition-opacity duration-150 ease-out motion-reduce:transition-none",
          open ? "opacity-100" : "opacity-0",
        )}
        inert={!open}
      >
        {children}
      </div>
    </div>
  );
}

export function parsePersonaBriefAiCell(cellKey: string): PersonaBriefAiEditCell | null {
  if (cellKey === "notes") return { target: "notes" };
  if (cellKey === "preferences") return { target: "preferences" };
  if (cellKey === "concept:direction") return { target: "concept", field: "direction" };
  if (cellKey === "concept:line") return { target: "concept", field: "line" };
  if (cellKey === "cta:direction") return { target: "cta", field: "direction" };
  if (cellKey === "cta:line") return { target: "cta", field: "line" };

  const hookMatch = /^hook:(\d+):(direction|line)$/.exec(cellKey);
  if (!hookMatch) return null;
  return {
    target: "hook",
    hookIndex: Number(hookMatch[1]),
    field: hookMatch[2] as "direction" | "line",
  };
}

export function PersonaBriefCard({
  brief,
  canEdit = false,
  isSelected = false,
  onToggleSelected,
  expanded: expandedProp,
  onExpandedChange,
  onPatch,
  onToggleFavorite,
  onOpenHistory,
  onAiEdit,
  isAiEditing = false,
  onApplyAi,
  aiEditKey = null,
  aiBusyKey = null,
  flashKey = null,
  onStartAi,
  onStopAi,
  searchQuery = "",
}: PersonaBriefCardProps) {
  const { t } = useTranslation();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = expandedProp ?? uncontrolledExpanded;
  function setExpanded(next: boolean) {
    onExpandedChange?.(next);
    if (expandedProp === undefined) setUncontrolledExpanded(next);
  }
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const { copied, copy } = useCopyToClipboard({ resetMs: PERSONA_SCRIPT_COPY_RESET_MS });
  const emptyValue = t("personaOpportunities.scripts.emptyValue", "—");
  const optionalEmpty = t(
    "personaOpportunities.scripts.briefs.optionalEmpty",
    "Not set — optional",
  );
  const orderedHooks = [...brief.hooks].sort((a, b) => a.hookIndex - b.hookIndex);
  const hookPackage = brief.hookPackage;
  const isOneHook = hookPackage === 1;
  const duration = brief.duration;
  const brandOverridden = isBrandOverridden(brief.brand);
  const isSaving = savingKey != null;

  async function persist(key: string, patch: PersonaBriefContentPatch) {
    if (!canEdit || !onPatch) return;
    setSavingKey(key);
    try {
      await onPatch(patch);
    } catch {
      // Mutation toast lives on the query hook.
    } finally {
      setSavingKey((current) => (current === key ? null : current));
    }
  }

  const showSendPreviewToast = () => {
    toast.info(
      t(
        "personaOpportunities.scripts.briefs.previewNotSaved",
        "Preview only — edits are not saved yet.",
      ),
    );
  };

  const handleCopy = async () => {
    const ok = await copy(formatPersonaBriefForCopy(brief, t));
    if (!ok) {
      toast.error(
        t(
          "personaOpportunities.scripts.briefs.copyError",
          "Couldn't copy the brief. Please try again.",
        ),
      );
      return;
    }
    toast.success(
      t("personaOpportunities.scripts.briefs.copyToast", "Brief copied to clipboard"),
    );
  };

  const handleShare = async () => {
    if (!(await writeClipboard(window.location.href))) {
      toast.error(
        t(
          "personaOpportunities.scripts.actions.shareError",
          "Couldn't copy the share link. Please try again.",
        ),
      );
      return;
    }
    toast.success(
      t(
        "personaOpportunities.scripts.briefs.shareToast",
        "Link copied — brief sharing is preview only.",
      ),
    );
  };

  const referenceHref = brief.creativeExampleUrl?.trim()
    ? briefReferenceHref(brief.creativeExampleUrl.trim())
    : null;
  const referenceLabel = t(
    "personaOpportunities.scripts.briefs.referenceLink",
    "Top creative for this angle",
  );
  const durationLabel = t("personaOpportunities.scripts.briefs.duration", "Duration");
  const hookPackageLabel = t("personaOpportunities.scripts.briefs.hookPackage", "Hook package");
  const ageLabel = t("personaOpportunities.scripts.briefs.ageRange", "Age range");
  const genderLabel = t("personaOpportunities.scripts.briefs.gender", "Gender");
  const hookPackageOneLabel = t("personaOpportunities.scripts.briefs.hookPackageOne", "1 Hook");
  const hookPackageFourLabel = t("personaOpportunities.scripts.briefs.hookPackageFour", "4 Hooks");
  const formatClosedListValue = (option: string) =>
    translatePersonaBriefClosedListValue(t, option);

  function renderBriefCell(
    cellKey: string,
    value: string,
    className?: string,
    emptyLabel?: string,
  ) {
    return (
      <div className={className}>
        <PersonaScriptEditableCell
          value={value}
          canEdit={canEdit}
          showAi={canEdit && !!onApplyAi}
          emptyLabel={emptyLabel}
          rows={3}
          highlightQuery={searchQuery}
          isEditing={editingKey === cellKey}
          isSaving={savingKey === cellKey}
          isAiOpen={aiEditKey === cellKey}
          isAiBusy={aiBusyKey === cellKey}
          isFlashing={flashKey === cellKey}
          onStartEdit={() => {
            onStopAi?.();
            setEditingKey(cellKey);
          }}
          onStopEdit={() =>
            setEditingKey((current) => (current === cellKey ? null : current))
          }
          onStartAi={() => {
            setEditingKey(null);
            onStartAi?.(cellKey);
          }}
          onStopAi={onStopAi}
          onApplyAi={(instruction) => onApplyAi?.(cellKey, instruction)}
          onSave={(next) => {
            const patch = patchForCell(cellKey, next);
            if (patch) void persist(cellKey, patch);
          }}
        />
      </div>
    );
  }

  return (
    <article
      className={cn(
        "group/brief overflow-hidden rounded-md border bg-background",
        isSelected ? "border-primary bg-primary/5" : "border-border",
      )}
      data-testid="persona-brief-card"
    >
      <header
        className="flex cursor-pointer items-start gap-[11px] px-4 py-3.5"
        onClick={() => setExpanded(!expanded)}
      >
        {onToggleSelected ? (
          <PersonaOpportunitySelectCheckbox
            checked={isSelected}
            onToggle={onToggleSelected}
            label={t("personaOpportunities.scripts.briefs.selectBrief", "Select brief")}
            className="mt-px shrink-0"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <Badge
              variant="ai"
              iconStart="file-text"
              className="mt-px gap-[5px] px-2 py-0.5 text-[10.5px] font-medium tracking-[0.04em] uppercase [&>svg]:size-[11px]"
            >
              {t("personaOpportunities.scripts.briefs.badgeShort", "BRIEF")}
            </Badge>
            <button
              type="button"
              aria-pressed={brief.isFavorite}
              aria-label={
                brief.isFavorite
                  ? t(
                      "personaOpportunities.scripts.briefs.unfavoriteAria",
                      "Remove from favorites",
                    )
                  : t("personaOpportunities.scripts.briefs.favoriteAria", "Add to favorites")
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!canEdit) return;
                onToggleFavorite?.();
              }}
              className={cn(
                "mt-px inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
                brief.isFavorite
                  ? "text-warning"
                  : "text-muted-foreground hover:text-warning",
              )}
            >
              <Icon
                name="star"
                size="sm"
                color="inherit"
                className={brief.isFavorite ? "fill-warning text-warning" : undefined}
                aria-hidden
              />
            </button>
            <div className="min-w-0 flex-1" onClick={(event) => event.stopPropagation()}>
              <PersonaScriptTitleEditor
                title={brief.videoName}
                canEdit={canEdit}
                highlightQuery={searchQuery}
                editAria={t(
                  "personaOpportunities.scripts.briefs.editTitleAria",
                  "Edit brief title",
                )}
                isEditing={editingKey === "title"}
                isSaving={savingKey === "title"}
                onStartEdit={() => {
                  onStopAi?.();
                  setEditingKey("title");
                }}
                onStopEdit={() =>
                  setEditingKey((current) => (current === "title" ? null : current))
                }
                onSave={(next) => {
                  void persist("title", { target: "videoName", value: next });
                }}
              />
            </div>
          </div>
          <p className="mt-[3px] text-xs italic text-muted-foreground">
            {t("personaOpportunities.scripts.briefs.kindMeta", {
              defaultValue_one: "Creator brief · {{hooks}} hook",
              defaultValue_other: "Creator brief · {{hooks}} hooks",
              count: hookPackage,
              hooks: hookPackage,
            })}
          </p>
          <p className="mt-0.5 text-[11.5px] tracking-[-0.01em] tabular-nums text-muted-foreground">
            <HighlightText
              text={t("personaOpportunities.scripts.briefs.meta", {
                defaultValue_one: "{{hooks}} hook · {{creators}} creator · {{duration}}",
                defaultValue_other: "{{hooks}} hooks · {{creators}} creator · {{duration}}",
                count: hookPackage,
                duration,
                hooks: hookPackage,
                creators: CREATOR_COUNT_DISPLAY,
              })}
              query={searchQuery}
            />
          </p>
        </div>
        {brief.version > 1 ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t(
                    "personaOpportunities.scripts.history.aria",
                    "Version history",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditingKey(null);
                    onStopAi?.();
                    onOpenHistory?.();
                  }}
                  className="mt-[3px] inline-flex h-[26px] min-h-11 shrink-0 items-center gap-[5px] rounded-sm border border-border bg-background px-2 text-[11px] tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-[26px]"
                >
                  <Icon name="history" size="sm" color="inherit" aria-hidden />
                  {brief.version}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("personaOpportunities.scripts.history.aria", "Version history")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("personaOpportunities.scripts.briefs.collapse", {
                  defaultValue: "Collapse {{title}}",
                  title: brief.videoName,
                })
              : t("personaOpportunities.scripts.briefs.expand", {
                  defaultValue: "Expand {{title}}",
                  title: brief.videoName,
                })
          }
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(!expanded);
          }}
          className="mt-[3px] inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
        >
          <Icon
            name="chevron-right"
            size="sm"
            className={cn(
              "text-muted-foreground/70 transition-transform duration-150 ease-out motion-reduce:transition-none",
              expanded && "rotate-90",
            )}
            aria-hidden
          />
        </button>
      </header>

      <CollapsibleReveal open={expanded}>
      <div className="border-t border-border">
      <div className="flex flex-wrap items-center gap-x-[22px] gap-y-2 border-b border-border bg-muted px-4 py-[11px]">
        <div className="flex min-w-0 items-center gap-[9px]">
          <FieldLabel>{t("personaOpportunities.scripts.briefs.reference", "Reference")}</FieldLabel>
          {referenceHref ? (
            <a
              href={referenceHref}
              target="_blank"
              rel="noreferrer"
              className={cn("truncate text-[12.5px] no-underline hover:underline", BODY_TEXT)}
            >
              {referenceLabel}
            </a>
          ) : (
            <span className="text-[12.5px] text-muted-foreground">{emptyValue}</span>
          )}
        </div>
        <div className="flex items-center gap-[9px]">
          <FieldLabel>{durationLabel}</FieldLabel>
          <ClosedListSelect
            ariaLabel={durationLabel}
            value={duration}
            searchQuery={searchQuery}
            options={PERSONA_BRIEF_DURATION_OPTIONS}
            formatOption={formatClosedListValue}
            readOnly={!canEdit}
            disabled={isSaving}
            onValueChange={(next) => {
              if (next === duration) return;
              void persist("duration", { target: "duration", value: next });
            }}
          />
        </div>
        <div className="flex items-center gap-[9px]">
          <FieldLabel>{hookPackageLabel}</FieldLabel>
          {canEdit ? (
            <Select
              value={String(hookPackage)}
              disabled={isSaving}
              onValueChange={(next) => {
                const parsed = parseHookPackage(next);
                if (parsed == null || parsed === hookPackage) return;
                void persist("hookPackage", { target: "hookPackage", value: parsed });
              }}
            >
              <SelectTrigger aria-label={hookPackageLabel} className={CHROME_SELECT_TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERSONA_BRIEF_HOOK_PACKAGE_OPTIONS.map((pkg) => (
                  <SelectItem key={pkg} value={String(pkg)}>
                    {pkg === 1 ? hookPackageOneLabel : hookPackageFourLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-[12.5px] text-foreground">
              {hookPackage === 1 ? hookPackageOneLabel : hookPackageFourLabel}
            </span>
          )}
        </div>
      </div>

      <div className={cn(COL_GRID, "hidden border-b border-border bg-background sm:grid")}>
        <span className={cn(MONO_LABEL, "px-4 py-[9px] pr-3")} />
        <span className={cn(MONO_LABEL, "px-3 py-[9px]")}>
          {t("personaOpportunities.scripts.briefs.directionColumn", "Creator direction")}
        </span>
        <span className={cn(MONO_LABEL, "py-[9px] pr-4 pl-3")}>
          {t("personaOpportunities.scripts.briefs.lineColumn", "Line to say on camera")}
        </span>
      </div>

      <section>
        <div className="flex flex-wrap items-baseline gap-2.5 px-4 pt-3 pb-[7px]">
          <h4 className="text-[13px] font-semibold text-foreground">
            {t("personaOpportunities.scripts.briefs.hooksTitle", "Hooks")}
          </h4>
          <p className="text-[11.5px] text-muted-foreground">
            {isOneHook
              ? t(
                  "personaOpportunities.scripts.briefs.hooksHintOne",
                  "One hook is sent — pick which",
                )
              : t(
                  "personaOpportunities.scripts.briefs.hooksHint",
                  "Four options — the vendor films all four",
                )}
          </p>
        </div>
        <ul>
          {orderedHooks.map((hook, index) => {
            const isSelected = brief.selectedHookIndex === hook.hookIndex;
            const isDimmed = isOneHook && !isSelected;
            return (
              <li
                key={hook.id}
                className={cn(
                  COL_GRID,
                  "border-b border-border-subtle",
                  hookRowClass(index),
                  isDimmed && "opacity-40",
                )}
              >
                <div className="flex items-center gap-2 py-[11px] pr-3 pl-4">
                  {isOneHook ? (
                    <input
                      type="radio"
                      name={`persona-brief-${brief.id}-selected-hook`}
                      className="size-3.5 shrink-0 accent-primary"
                      checked={isSelected}
                      disabled={!canEdit || isSaving}
                      aria-label={t("personaOpportunities.scripts.briefs.selectHook", {
                        defaultValue: "Select hook {{index}}",
                        index: hook.hookIndex,
                      })}
                      onChange={() => {
                        if (isSelected) return;
                        void persist("selectedHookIndex", {
                          target: "selectedHookIndex",
                          value: hook.hookIndex,
                        });
                      }}
                    />
                  ) : null}
                  <span className="grid size-[22px] place-items-center rounded-full bg-border-subtle text-[11px] tabular-nums text-secondary-subtle-foreground">
                    {hook.hookIndex}
                  </span>
                </div>
                {renderBriefCell(
                  `hook:${hook.hookIndex}:direction`,
                  hook.direction,
                  "px-1 py-1",
                )}
                {renderBriefCell(`hook:${hook.hookIndex}:line`, hook.line, "px-1 py-1 pr-3")}
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <div className="flex flex-wrap items-baseline gap-2.5 px-4 pt-[15px] pb-[7px]">
          <h4 className="text-[13px] font-semibold text-foreground">
            {t("personaOpportunities.scripts.briefs.conceptTitle", "Concept")}
          </h4>
          <p className="text-[11.5px] text-muted-foreground">
            {t("personaOpportunities.scripts.briefs.conceptHint", "The body of the video")}
          </p>
        </div>
        <div className={cn(COL_GRID, "border-b border-border-subtle")}>
          <div />
          {renderBriefCell("concept:direction", brief.concept.direction, "px-1 py-1")}
          {renderBriefCell("concept:line", brief.concept.line, "px-1 py-1 pr-3")}
        </div>
      </section>

      <section>
        <h4 className="px-4 pt-[15px] pb-[7px] text-[13px] font-semibold text-foreground">
          {t("personaOpportunities.scripts.briefs.ctaTitle", "Call to action")}
        </h4>
        <div className={cn(COL_GRID, "border-b border-border")}>
          <div />
          {renderBriefCell("cta:direction", brief.cta.direction, "px-1 py-1")}
          {renderBriefCell("cta:line", brief.cta.line, "px-1 py-1 pr-3")}
        </div>
      </section>

      <div className="grid grid-cols-1 border-b border-border sm:grid-cols-2">
        <section className="border-b border-border px-4 py-[15px] sm:border-r sm:border-b-0">
          <h4 className="mb-2.5 text-[13px] font-semibold text-foreground">
            {t("personaOpportunities.scripts.briefs.profileTitle", "Creator profile")}
          </h4>
          <div className="flex flex-col gap-[9px] text-[12.5px]">
            <div className="flex items-center gap-3">
              <span className="w-[104px] shrink-0 text-muted-foreground">{ageLabel}</span>
              <ClosedListSelect
                ariaLabel={ageLabel}
                value={brief.creatorProfile.ageRange}
                searchQuery={searchQuery}
                options={PERSONA_BRIEF_AGE_RANGE_OPTIONS}
                formatOption={formatClosedListValue}
                readOnly={!canEdit}
                disabled={isSaving}
                onValueChange={(next) => {
                  if (next === brief.creatorProfile.ageRange) return;
                  void persist("ageRange", { target: "ageRange", value: next });
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="w-[104px] shrink-0 text-muted-foreground">{genderLabel}</span>
              <ClosedListSelect
                ariaLabel={genderLabel}
                value={brief.creatorProfile.gender}
                searchQuery={searchQuery}
                options={PERSONA_BRIEF_GENDER_OPTIONS}
                formatOption={formatClosedListValue}
                readOnly={!canEdit}
                disabled={isSaving}
                onValueChange={(next) => {
                  if (next === brief.creatorProfile.gender) return;
                  if (next !== "Male" && next !== "Female" && next !== "Both") return;
                  void persist("gender", { target: "gender", value: next });
                }}
              />
            </div>
            <div className="flex items-start gap-3">
              <span className="w-[104px] shrink-0 pt-2 text-muted-foreground">
                {t("personaOpportunities.scripts.briefs.preferences", "Preferences")}
              </span>
              <div className="min-w-0 flex-1">
                {renderBriefCell(
                  "preferences",
                  brief.creatorProfile.preferences ?? "",
                  undefined,
                  optionalEmpty,
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="px-4 py-[15px]">
          <h4 className="mb-[9px] text-[13px] font-semibold text-foreground">
            {t("personaOpportunities.scripts.briefs.notesTitle", "Notes for the creator")}
          </h4>
          {renderBriefCell("notes", brief.notes ?? "", undefined, optionalEmpty)}
        </section>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2">
        <section className="border-b border-border px-4 py-[15px] sm:border-r sm:border-b-0">
          <div className="mb-[5px] flex flex-wrap items-center gap-[9px]">
            <h4 className="text-[13px] font-semibold text-foreground">
              {t("personaOpportunities.scripts.briefs.brandTitle", "Brand")}
            </h4>
            <span className="rounded-sm bg-[var(--slate-chip-bg)] px-[7px] py-px text-[10.5px] text-[var(--slate-chip-text)]">
              {brandOverridden
                ? t("personaOpportunities.scripts.briefs.overridden", "Overridden")
                : t("personaOpportunities.scripts.briefs.inherited", "Inherited")}
            </span>
            {canEdit && brandOverridden ? (
              <Button
                type="button"
                size="xs"
                variant="secondary"
                disabled={isSaving}
                onClick={() => void persist("resetBrand", { target: "resetBrand" })}
              >
                {t("personaOpportunities.scripts.briefs.resetToWorkspace", "Reset to workspace")}
              </Button>
            ) : null}
          </div>
          <p className="mb-2.5 text-[11.5px] leading-[1.55] text-muted-foreground">
            {t(
              "personaOpportunities.scripts.briefs.inheritedHint",
              "Same on every brief. Override only if this one differs.",
            )}
          </p>
          <div className="flex flex-col gap-0.5">
            {canEdit ? (
              <>
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-product-link`}
                  label={t("personaOpportunities.scripts.briefs.productLink", "Product link")}
                  value={brief.brand.productLink}
                  disabled={isSaving}
                  onCommit={(next) =>
                    void persist("productLink", { target: "productLink", value: next })
                  }
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-font`}
                  label={t("personaOpportunities.scripts.briefs.font", "Font")}
                  value={brief.brand.font}
                  disabled={isSaving}
                  onCommit={(next) => void persist("font", { target: "font", value: next })}
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-logo`}
                  label={t("personaOpportunities.scripts.briefs.logo", "Logo")}
                  value={brief.brand.logo}
                  disabled={isSaving}
                  onCommit={(next) => void persist("logo", { target: "logo", value: next })}
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-disclaimer`}
                  label={t("personaOpportunities.scripts.briefs.disclaimer", "Disclaimer")}
                  value={brief.brand.disclaimer}
                  disabled={isSaving}
                  onCommit={(next) =>
                    void persist("disclaimer", { target: "disclaimer", value: next })
                  }
                />
              </>
            ) : (
              <>
                <BrandFieldRow
                  label={t("personaOpportunities.scripts.briefs.productLink", "Product link")}
                  value={brief.brand.productLink?.trim() || emptyValue}
                />
                <BrandFieldRow
                  label={t("personaOpportunities.scripts.briefs.font", "Font")}
                  value={brief.brand.font?.trim() || emptyValue}
                />
                <BrandFieldRow
                  label={t("personaOpportunities.scripts.briefs.logo", "Logo")}
                  value={brief.brand.logo?.trim() || emptyValue}
                />
                <BrandFieldRow
                  label={t("personaOpportunities.scripts.briefs.disclaimer", "Disclaimer")}
                  value={brief.brand.disclaimer?.trim() || emptyValue}
                />
              </>
            )}
          </div>
        </section>
        <section className="bg-muted px-4 py-[15px]">
          <div className="mb-[5px] flex flex-wrap items-center gap-[9px]">
            <h4 className="text-[13px] font-semibold text-foreground">
              {t("personaOpportunities.scripts.briefs.orderTitle", "Order details")}
            </h4>
            <span className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-px text-[10.5px] text-muted-foreground">
              <Icon name="lock" size="xs" color="inherit" aria-hidden />
              {t("personaOpportunities.scripts.briefs.chosenAtSend", "Chosen at send")}
            </span>
          </div>
          <p className="mb-2.5 text-[11.5px] leading-[1.55] text-muted-foreground">
            {t(
              "personaOpportunities.scripts.briefs.orderHint",
              "These depend on budget and campaign, not the creative — you set them when you send. The same brief can go out more than once.",
            )}
          </p>
          <div className="grid grid-cols-[118px_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-[7px] text-[12.5px]">
            <span className="text-muted-foreground">
              {t("personaOpportunities.scripts.briefs.aspectRatio", "Aspect ratio")}
            </span>
            <span className={BODY_TEXT}>{emptyValue}</span>
            <span className="text-muted-foreground">
              {t("personaOpportunities.scripts.briefs.videoCount", "Number of videos")}
            </span>
            <span className="text-muted-foreground">{emptyValue}</span>
            <span className="text-muted-foreground">
              {t("personaOpportunities.scripts.briefs.productShipped", "Product shipped")}
            </span>
            <span className="text-muted-foreground">{emptyValue}</span>
          </div>
        </section>
      </div>

      <footer className="flex flex-col gap-2 border-t border-border bg-muted px-4 py-3">
        {isAiEditing ? (
          <p
            role="status"
            className="flex items-center gap-2 rounded-md bg-[var(--ai-fill-light)] px-3 py-2 text-xs text-[var(--ai-text)]"
          >
            <Icon name="loader" color="inherit" size="sm" className="animate-spin" />
            {t(
              "personaOpportunities.scripts.briefs.aiEditor.editing",
              "AI editing this brief...",
            )}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2.5">
        <Button
          type="button"
          variant="secondary"
          className={ICON_BUTTON_CLASS}
          iconStart="share"
          onClick={() => void handleShare()}
          aria-label={t("personaOpportunities.scripts.actions.share", "Share")}
        />
        <Button
          type="button"
          variant="secondary"
          className={ICON_BUTTON_CLASS}
          iconStart={copied ? "check" : "copy"}
          onClick={() => void handleCopy()}
          aria-label={
            copied
              ? t("personaOpportunities.scripts.actions.copied", "Copied!")
              : t("personaOpportunities.scripts.briefs.copy", "Copy brief")
          }
        />
        {canEdit && onAiEdit ? (
          <Button
            type="button"
            variant="ai"
            iconStart={isAiEditing ? "loader" : "sparkles"}
            disabled={isAiEditing}
            aria-busy={isAiEditing}
            onClick={onAiEdit}
            className={isAiEditing ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}` : AI_BUTTON_CLASS}
          >
            {t("personaOpportunities.scripts.actions.aiEdit", "AI Edit")}
          </Button>
        ) : null}
        <span className="min-w-2 flex-1" />
        <Button type="button" className={SEND_BUTTON_CLASS} onClick={showSendPreviewToast}>
          <PaperPlaneIcon />
          {t("personaOpportunities.scripts.briefs.send", "Send to production")}
        </Button>
        </div>
      </footer>
      </div>
      </CollapsibleReveal>
    </article>
  );
}

function patchForCell(cellKey: string, value: string): PersonaBriefContentPatch | null {
  if (cellKey === "notes") return { target: "notes", value };
  if (cellKey === "preferences") return { target: "preferences", value };
  if (cellKey === "concept:direction") return { target: "concept", field: "direction", value };
  if (cellKey === "concept:line") return { target: "concept", field: "line", value };
  if (cellKey === "cta:direction") return { target: "cta", field: "direction", value };
  if (cellKey === "cta:line") return { target: "cta", field: "line", value };

  const hookMatch = /^hook:(\d+):(direction|line)$/.exec(cellKey);
  if (!hookMatch) return null;
  return {
    target: "hook",
    hookIndex: Number(hookMatch[1]),
    field: hookMatch[2] as "direction" | "line",
    value,
  };
}
